import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import useWishList from '../../hooks/useOfflineWishlist'
import register from 'preact-custom-element'
import LoaderSpin from '../loader-spin/loader-spin'
import { storefront } from 'libs/storefront/client'
import { GET_PRODUCTS_BY_IDS } from '@/components/libs/storefront/product.query'
import PreactImage from 'snippets/image/image'
import Icon from '@/components/snippets/icon/icon'

/** GLOBAL VARIABLES */
const PRODUCT_GUI_PREFIX = 'gid://shopify/Product/'
const VIEW_STATUS_VALUES = {
  LOADING: 'LOADING',
  READY: 'READY',
  NO_DATA: 'NO_DATA',
  ERROR: 'ERROR'
}

const parseMoney = (value) => {
  const float = parseFloat(value)
  const fixedValue = float.toFixed(2)
  return `$${fixedValue}`
}

const WishListIcon = ({ productId, wishlist }) => {
  const removeWishlistItem = () => {
    wishlist.wsCallbacks.removeWishlistItem(wishlist.key.pop())
  }
  return (
    <div onClick={removeWishlistItem} className={'absolute right-4 top-4 z-[1] cursor-pointer'}>
      <Icon id={'heart-filled'} className={'z-30 min-h-6 min-w-6'} />
    </div>
  )
}

/**
 *
 * @param {string[]} items String[]
 */
const parseIdToGID = (items) => {
  return items.map((item) => PRODUCT_GUI_PREFIX.concat(item))
}

const getIdFromGid = (productGID) => productGID.split('/').pop()

/**
 * Data Structure
 * items : String[]
 * String structure  product_id:variant_id
 *  */
const parseWishlistItems = (items) => {
  if (!items || !items.length) {
    return {}
  }

  const products = items.reduce((acc, item) => {
    if (!item.includes(':')) return acc
    const [product, variant] = item.split(':')

    if (!variant) {
      return acc
    }
    if (acc.has(product)) {
      acc.get(product).push(variant)
      return acc
    }
    acc.set(product, [variant])
    return acc
  }, new Map())

  return Object.fromEntries(products.entries())
}

const VERSION = 2

const mapWishListDataToProductData = (items, products) => {
  const productMap = items.reduce((acc, i) => {
    const [productId, _] = i.split(':')
    acc[productId] ? acc[productId].push(i) : (acc[productId] = [i])
    return acc
  }, {})

  const wishlistProductData = products
    .map((p) => {
      const id = getIdFromGid(p.id)
      const key = productMap[id]
      const tmp = { ...p }
      if (!key) return undefined

      if (VERSION === 2) {
        const productClone = key.reduce((acc, k) => {
          const tmp = {
            ...p,
            wishlist: [k]
          }
          acc.push(tmp)
          return acc
        }, [])

        return [...productClone]
      } else {
        tmp.wishlist = key
        return tmp
      }
    })
    .filter((p) => p)

  if (VERSION === 2) {
    return wishlistProductData.flat()
  }

  return wishlistProductData
}

function WishListView() {
  const { wishlist, ...callbacks } = useWishList()
  const [config, setConfig] = useState({
    items: parseWishlistItems(wishlist),
    status: VIEW_STATUS_VALUES.LOADING,
    productData: []
  })

  useEffect(() => {
    setConfig((c) => ({
      ...c,
      status: VIEW_STATUS_VALUES.LOADING
    }))
    const items = Object.keys(config.items)
    if (!items.length) {
      setConfig((c) => ({
        ...c,
        status: VIEW_STATUS_VALUES.NO_DATA
      }))
      return
    }
    /**
     * Convert product ids into shopify GID
     */
    const ids = parseIdToGID(items)
    storefront
      .query({
        query: GET_PRODUCTS_BY_IDS,
        variables: {
          ids,
          metafieldIdentifiers: [
            {
              namespace: 'utopia',
              key: 'customer_facing_product_name'
            },
            {
              namespace: 'utopia',
              key: 'centimeter'
            },
            {
              namespace: 'utopia',
              key: 'kids_us'
            },
            {
              namespace: 'utopia',
              key: 'kids_uk'
            },
            {
              namespace: 'utopia',
              key: 'euro'
            },
            {
              namespace: 'utopia',
              key: 'age_group'
            },
            {
              namespace: 'omneo',
              key: 'brand'
            }
          ]
        }
      })
      .then((value) => {
        const productData = mapWishListDataToProductData(wishlist, value.data.nodes)
        if (!productData.length) {
          setConfig((c) => ({
            ...c,
            status: VIEW_STATUS_VALUES.NO_DATA
          }))
          return
        }
        setConfig((c) => ({
          ...c,
          status: VIEW_STATUS_VALUES.READY,
          productData
        }))
      })
      .catch((err) => {
        console.log(err)
        setConfig((c) => ({
          ...c,
          status: VIEW_STATUS_VALUES.ERROR
        }))
      })
  }, [wishlist])

  switch (config.status) {
    case VIEW_STATUS_VALUES.LOADING: {
      return (
        <div className={'relative flex h-40 w-screen items-center justify-center'}>
          <LoaderSpin />
        </div>
      )
    }
    case VIEW_STATUS_VALUES.NO_DATA: {
      return (
        <div className={'flex h-full w-full flex-col items-center justify-center py-6'}>
          <h4 class="h4 text-center font-bold">Your Wishlist is empty</h4>
        </div>
      )
    }
    case VIEW_STATUS_VALUES.READY: {
      return <WishlistItems products={config.productData} wsCallbacks={callbacks} />
    }
    case VIEW_STATUS_VALUES.ERROR: {
      return (
        <div className={'flex h-full w-full flex-col items-center justify-center py-6'}>
          <h4 class="h4 text-center font-bold">Something went wrong.</h4>
        </div>
      )
    }
  }
}

const productTitle = (product) => {
  if (!product) {
    return {
      title: '',
      colour: ''
    }
  }
  const title = product?.title
  const lastIndex = title.lastIndexOf(' ')

  return {
    title: title.slice(0, lastIndex),
    colour: title.slice(lastIndex + 1, title.length)
  }
}

const getVariantSimilarToSelected = (current, width, options) => {
  const currentVariant = options.sizes.find((s) => current === s.id)
  const newSizeOptions = options.sizes.filter((s) => s.width === width)
  const foundOption = newSizeOptions.find((o) => o.name === currentVariant.name)
  if (foundOption) return foundOption
  return currentVariant
}

const handleSelected = (options, wishlist) => {
  const variantIds = wishlist.map((item) => item.split(':').pop())
  const firstSelectedItem = options.sizes.find((s) => variantIds.includes(s.id))
  return firstSelectedItem
}
const VariantOptions = ({ options, wishlist, onSelectionChange }) => {
  const [firstRender, setFirstRender] = useState(true)
  const handleFirstRender = () => {
    if (!firstRender) return
    setFirstRender(false)
  }
  const hasSelected = useMemo(() => handleSelected(options, wishlist), [])
  const [view, setView] = useState(() => {
    if (hasSelected) {
      return hasSelected.width
    }

    return options?.width?.length ? options.widths[0] : ''
  })
  const [config, setConfig] = useState({
    selectedId: hasSelected ? hasSelected?.id : ''
  })

  useEffect(() => {
    if (!config.selectedId) return
    const selected = options.sizes.find((s) => s.id === config.selectedId)
    if (onSelectionChange && selected) onSelectionChange(selected)
  }, [config.selectedId])

  useEffect(() => {
    if (!firstRender && view !== '') {
      const newId = getVariantSimilarToSelected(config.selectedId, view, options)
      setConfig((c) => ({
        ...c,
        selectedId: newId.id
      }))
    }
  }, [view])

  return (
    <div className={'flex w-full flex-row-reverse gap-2 xl-max:flex-wrap'}>
      {!!config.selectedId && (
        <>
          <input type="hidden" name="id" value={config.selectedId} />
          <input type="hidden" name="quantity" value="1" />
        </>
      )}
      {options.widths.length >= 1 && (
        <>
          <div className={'flex w-full flex-col gap-1'}>
            <label className={'paragraph-sm font-bold'}>Shoe Width</label>
            <select
              value={view}
              onChange={(e) => {
                const width = e.currentTarget.value

                setView(() => width)
                handleFirstRender()
              }}
              className={'w-full rounded-lg'}
            >
              {options.widths.map((w) => {
                return <option>{w}</option>
              })}
            </select>
          </div>
        </>
      )}
      <div className={'flex w-full flex-col gap-1'}>
        <label className={'paragraph-sm font-bold'}>Shoe Size</label>
        <select
          value={config.selectedId}
          onChange={(e) => {
            const { value } = e.currentTarget
            setConfig((c) => ({
              ...c,
              selectedId: value
            }))
          }}
          className={'w-full rounded-lg'}
        >
          {options.sizes
            .filter((s) => {
              if (!view) return true
              return view === s.width
            })
            .map((s) => {
              return (
                <option disabled={!s.availableForSale} value={s.id}>
                  {s.name}
                </option>
              )
            })}
        </select>
      </div>
    </div>
  )
}

const splitId = (gid) => {
  return gid.split('/').pop()
}

const buildOptionData = (product) => {
  /** Get Variant edge data  */
  const variants = product?.variants?.edges?.map(({ node }) => node) /**

  /** Reduce the variant data into its new data structure  */
  const optionData = variants.reduce(
    (acc, v) => {
      const variantOption = v.selectedOptions.reduce((acc, o) => {
        if (o.name === 'Size') {
          const value = o.value.split('/').shift()
          acc = {
            ...acc,
            name: value
          }
          return acc
        }
        if (o.name === 'Width') {
          acc = {
            ...acc,
            width: o.value
          }
        }
        return acc
      }, {})
      const id = splitId(v.id)
      const size = { ...variantOption, id, notInStock: v.currentlyNotInStock, availableForSale: v.availableForSale }
      acc.sizes.push(size)
      if (size?.width) {
        acc.widths.add(size.width)
      }

      return acc
    },
    {
      widths: new Set(),
      sizes: []
    }
  )
  const toArray = Array.from(optionData?.widths?.values())
  optionData.widths = toArray
  return optionData
}

const WishListItem = ({ product, wsCallbacks }) => {
  const { title, colour } = productTitle(product)
  const options = useMemo(() => {
    return buildOptionData(product)
  }, [])
  const [selectedVariant, setSelectedVariant] = useState(null)
  const handleFormSubmitCallback = useCallback((e) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const id = data.get('id')
    const quantity = data.get('quantity')

    window.Cart.addLineItems({
      id,
      quantity
    })
  }, [])

  const isSoldOut = selectedVariant ? (!selectedVariant.availableForSale || selectedVariant.notInStock) : false
  const disableOOS = isSoldOut

  return (
    <form onSubmit={handleFormSubmitCallback} className={'relative flex w-full flex-col gap-4'}>
      <WishListIcon wishlist={{ wsCallbacks, key: product.wishlist }} productId={getIdFromGid(product.id)} />
      <a href={`/products/${product.handle}`} data-role="image" class="relative aspect-1 w-full">
        <PreactImage
          imageClass="fit object-bottom"
          className={'h-full w-full object-cover object-bottom'}
          key={product.id}
          imageUrl={[product?.featuredImage?.url]}
        />
      </a>
      <div data-role="details">
        <div className={'flex flex-col gap-1 border-b pb-4'}>
          <a href={`/products/${product.handle}`} className={'paragraph-xs font-bold'}>
            {product.vendor}
          </a>
          <p className={'paragraph-sm text-neutrals-secondary-grey'}>{product.title}</p>
        </div>
        <div className={'flex flex-row justify-between gap-1 pt-4'}>
          <p className={'paragraph-sm font-bold'}>{parseMoney(product?.priceRange?.maxVariantPrice?.amount)}</p>
          <p className={'paragraph-sm text-neutrals-secondary-grey'}>{colour}</p>
        </div>
      </div>
      <div className={'flex w-full flex-col gap-4 pt-6'}>
        <div data-role="options">
          <VariantOptions options={options} wishlist={product.wishlist} onSelectionChange={setSelectedVariant} />
        </div>
        <button className={'btn btn-inline btn-md btn-solid-black justify-center'} disabled={disableOOS}>
          {disableOOS ? 'Out of Stock' : 'Add To Bag'}
        </button>
      </div>
    </form>
  )
}

/**
 * Wishlist Items
 * Props
 *  dataCustomer - {string | null}
 * @param {*} props
 */
function WishlistItems({ products, wsCallbacks }) {
  return (
    <div className={'grid grid-cols-2 gap-4 px-10 py-10 md:grid-cols-4'}>
      {products.map((product) => (
        <WishListItem product={product} wsCallbacks={wsCallbacks} />
      ))}
    </div>
  )
}

register(WishListView, 'x-wishlist-items')
